import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { DashboardDensity, isCompact } from '../../utils/dashboardDensity';

interface OperationalHealthBannerProps {
  health?: { level: string; message: string; icon?: string };
  lastUpdated?: string;
  density?: DashboardDensity;
}

const LEVEL_CONFIG: Record<string, { severity: 'success' | 'warning' | 'error' | 'info'; icon: React.ReactNode; bgcolor: string }> = {
  green: { severity: 'success', icon: <CheckCircleIcon fontSize="small" />, bgcolor: '#e8f5e9' },
  yellow: { severity: 'warning', icon: <ScheduleIcon fontSize="small" />, bgcolor: '#fff8e1' },
  orange: { severity: 'warning', icon: <WarningIcon fontSize="small" />, bgcolor: '#fff3e0' },
  red: { severity: 'error', icon: <ErrorIcon fontSize="small" />, bgcolor: '#ffebee' },
};

const OperationalHealthBanner: React.FC<OperationalHealthBannerProps> = ({ health, lastUpdated, density = 'standard' }) => {
  if (!health) return null;
  const config = LEVEL_CONFIG[health.level] || LEVEL_CONFIG.green;
  const compact = isCompact(density);

  return (
    <Alert
      severity={config.severity}
      icon={config.icon}
      sx={{
        py: compact ? 0 : 0.25,
        px: compact ? 1 : 1.5,
        bgcolor: config.bgcolor,
        border: '1px solid',
        borderColor: 'divider',
        '& .MuiAlert-message': { py: compact ? 0.25 : 0.5, width: '100%' },
        '& .MuiAlert-icon': { py: compact ? 0.5 : 0.75 },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" gap={1}>
        <Typography variant="caption" fontWeight={600} sx={{ fontSize: compact ? '0.7rem' : '0.8rem', lineHeight: 1.3 }}>
          {health.message}
        </Typography>
        {lastUpdated && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {new Date(lastUpdated).toLocaleTimeString()}
          </Typography>
        )}
      </Box>
    </Alert>
  );
};

export default OperationalHealthBanner;
